import { get } from 'lodash';

/**
 * Various functions to manipulate sets
 */
export class SetHlp {
  /**
   * Create set of object ids
   * @param {T[]}    list
   * @param {string} path path to key, default 'id'
   * @return {Map<string, T>}
   */
  public static idSet(list: any[], path = 'id'): Set<string> {
    return new Set(list.map((x) => get(x, path)));
  }

  /**
   * Adds element to set in absent, removes otherwise
   * @param {Set<T>} set
   * @param {T}      elem
   * @return {boolean} if set has given elem AFTER operation
   */
  public static toggle<T>(set: Set<T>, elem: T): boolean {
    const hasElem = set.has(elem);
    if (hasElem) set.delete(elem);
    else set.add(elem);
    return !hasElem;
  }

  /**
   * Adds or removes element to set, depending on boolean parameter
   * @param {Set<T>}  set
   * @param {T}       elem
   * @param {boolean} value if true, 'elem' will be added, otherwise removed
   * @return {boolean} if set has changed
   */
  public static update<T>(set: Set<T>, elem: T, value: boolean): boolean {
    const oldValue = set.has(elem);
    if (value) set.add(elem);
    else set.delete(elem);
    return oldValue != value;
  }

  /**
   * Check if two sets contains exact same values
   * @param {Set<T>} a
   * @param {Set<T>} b
   * @return {boolean}
   */
  public static equals<T>(a: Set<T>, b: Set<T>): boolean {
    let eq = a.size == b.size;
    const aIterator = a[Symbol.iterator]();
    let aResult = aIterator.next();

    while (eq && !aResult.done) {
      eq = b.has(aResult.value);
      aResult = aIterator.next();
    }

    return eq;
  }

  /**
   * Check if set is defined and has given value
   * @param {Set<T>} set
   * @param {T}      elem
   * @return {boolean}
   */
  public static has<T>(set: Set<T> | null | undefined, elem: T): boolean {
    return set?.size ? set.has(elem) : false;
  }

  /**
   * Add multiple objects to set
   * @param {Set<T>} set
   * @param {T}      values
   */
  public static addMultiple<T>(set: Set<T>, values: T[]): void {
    values.forEach((v) => set.add(v));
  }
}
